import { defineCanFunction } from '@foodlista/access-control'
import { useQuery } from '@tanstack/vue-query'

export const USERS_CACHE_KEYS = {
  userProfile: ['users', 'me'],
  restaurants: ['users', 'me', 'restaurants'],
  ownedRestaurants: ['users', 'me', 'restaurants', 'owned'],
  invitations: ['users', 'me', 'invitations'],
  identities: ['users', 'me', 'identities'],
  termsAcceptance: (userId: string | undefined) => ['users', 'me', 'terms', userId],
}

export function useGetUserProfile() {
  const { $orpc } = useNuxtApp()

  const queryResult = useQuery({
    queryFn: () => $orpc.auth.me(),
    queryKey: USERS_CACHE_KEYS.userProfile,
  })

  const userWithAbilities = computed(() => {
    const user = queryResult.data.value

    if (!user) {
      return null
    }

    const can = defineCanFunction(user.abilities || [])

    return {
      ...user,
      can,
    }
  })

  return {
    ...queryResult,
    data: userWithAbilities,
  }
}

export function useGetTermsAcceptanceStatus(
  { userId, enabled }: { userId: Ref<string | undefined>, enabled: Ref<boolean> },
) {
  const { $orpc } = useNuxtApp()

  return useQuery({
    queryKey: computed(() => USERS_CACHE_KEYS.termsAcceptance(userId.value)),
    queryFn: () => $orpc.users.me.terms.getCurrentStatus(),
    enabled,
    throwOnError: false,
  })
}

export function useGetCurrentUserIdentities() {
  const { $orpc } = useNuxtApp()
  return useQuery({
    queryFn: () => $orpc.users.me.identities.list(),
    queryKey: USERS_CACHE_KEYS.identities,
  })
}

export function useGetCurrentUserInvitations() {
  const { $orpc } = useNuxtApp()
  return useQuery({
    queryKey: USERS_CACHE_KEYS.invitations,
    queryFn: () => $orpc.users.me.invitations(),
  })
}

export function useGetUserRestaurants() {
  const { $orpc } = useNuxtApp()

  return useQuery({
    queryFn: () => $orpc.users.me.restaurants.all(),
    queryKey: USERS_CACHE_KEYS.restaurants,
  })
}

export function useGetUserOwnedRestaurants() {
  const { $orpc } = useNuxtApp()

  return useQuery({
    queryFn: () => $orpc.users.me.restaurants.owned(),
    queryKey: USERS_CACHE_KEYS.ownedRestaurants,
  })
}
